'use client';

/**
 * KpiTile Component
 *
 * Compact KPI card for dashboard strips (4-up / 5-up grids). Differs from
 * StatCard by adding a tone-strip accent bar at the top (not bottom of value
 * row), keeping the value at body1/weight-600 so it never exceeds the page
 * title (h5, 16px) per portal typography rules.
 *
 * Reusable across portals — name is generic, NOT borrower-specific.
 *
 * Tone mapping (theme tokens, NO hex literals):
 *   mint   → primary.main (v3 platform accent)
 *   amber  → warning.main
 *   red    → error.main
 *   id     → secondary.main (purple — F.purple)
 *   orange → baseColors.orange[500] (F.orange — borrower category)
 *   blue   → info.main
 */

import Box from '@mui/material/Box';
import Skeleton from '@mui/material/Skeleton';
import { alpha, type SxProps, type Theme, useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import type { ReactNode } from 'react';
import { baseColors } from '../../theme/palette';
import { iconSizes } from '../../theme/typography';
import { composeSx } from '../../utils/styledUtils';

// ============================================================================
// Types
// ============================================================================

export type KpiTileTone = 'mint' | 'amber' | 'red' | 'id' | 'orange' | 'blue';

export interface KpiTileProps {
  /** Uppercase eyebrow label (e.g. "Active loans"). */
  label: ReactNode;
  /** Headline value — kept at body1/weight 600 (max 16px effective). */
  value: ReactNode;
  /** Sub-label rendered in caption below the value. */
  sub?: ReactNode;
  /** Tone — drives the top strip + icon color. */
  tone: KpiTileTone;
  /** Optional icon shown top-right (MUI SvgIcon). */
  icon?: ReactNode;
  /** Click handler — makes the tile keyboard-focusable. */
  onClick?: () => void;
  /**
   * Optional aria-label for the interactive (button-mode) tile. Required
   * when `label` or `value` carry icons / non-string ReactNodes — without
   * it, the accessible name would be empty for screen-reader users. When
   * label and value are both plain strings the rendered text serves as a
   * sufficient accessible name and the prop can be omitted.
   */
  ariaLabel?: string;
  /** Sx override. */
  sx?: SxProps<Theme>;
}

// ============================================================================
// Tone resolver
// ============================================================================

function resolveToneColor(theme: Theme, tone: KpiTileTone): string {
  switch (tone) {
    case 'mint':
      return theme.palette.primary.main;
    case 'amber':
      return theme.palette.warning.main;
    case 'red':
      return theme.palette.error.main;
    case 'id':
      return theme.palette.secondary.main;
    case 'orange':
      return baseColors.orange[500];
    case 'blue':
      return theme.palette.info.main;
    default:
      return theme.palette.primary.main;
  }
}

// ============================================================================
// Skeleton
// ============================================================================

function KpiTileSkeleton() {
  return (
    // Mirrors the main component's outer container exactly so the content
    // baseline doesn't shift when the real tile loads in. Two specific
    // things matter here: (1) `pt: 1.75` matches the main's extra 2px of
    // top padding that reserves space below the absolutely-positioned tone
    // strip, and (2) the tone-strip placeholder uses the same
    // `position: absolute, top/left/right: 0, height: 2` model rather than
    // the older negative-margin approach — keeping the paint flow identical.
    <Box
      sx={{
        position: 'relative',
        borderRadius: 1.5,
        border: '1px solid',
        borderColor: 'divider',
        backgroundColor: 'background.paper',
        p: 1.5,
        pt: 1.75,
        overflow: 'hidden',
      }}
    >
      <Box
        aria-hidden
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: 2,
          backgroundColor: 'divider',
        }}
      />
      <Skeleton variant="text" width="60%" height={14} sx={{ mb: 0.5 }} />
      <Skeleton variant="text" width="40%" height={22} />
      <Skeleton variant="text" width="70%" height={14} />
    </Box>
  );
}

// ============================================================================
// Main Component
// ============================================================================

export function KpiTile({ label, value, sub, tone, icon, onClick, ariaLabel, sx }: KpiTileProps) {
  const theme = useTheme();
  const color = resolveToneColor(theme, tone);
  const interactive = !!onClick;

  return (
    <Box
      component={interactive ? 'button' : 'div'}
      type={interactive ? 'button' : undefined}
      onClick={onClick}
      aria-label={interactive ? ariaLabel : undefined}
      sx={composeSx(
        {
          position: 'relative',
          width: '100%',
          minHeight: 44,
          textAlign: 'left',
          fontFamily: 'inherit',
          appearance: 'none',
          border: '1px solid',
          borderColor: 'divider',
          backgroundColor: 'background.paper',
          borderRadius: 1.5,
          p: 1.5,
          pt: 1.75,
          overflow: 'hidden',
          cursor: interactive ? 'pointer' : 'default',
          transition: theme.transitions.create(['background-color', 'border-color'], { duration: 150 }),
          '&:hover': interactive
            ? {
                backgroundColor: alpha(color, 0.04),
                borderColor: alpha(color, 0.32),
              }
            : undefined,
          '&:focus-visible': interactive
            ? {
                outline: `2px solid ${color}`,
                outlineOffset: 2,
              }
            : undefined,
        },
        sx,
      )}
    >
      {/* Tone strip — 2px accent across the top edge */}
      <Box
        aria-hidden
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: 2,
          backgroundColor: color,
        }}
      />

      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1 }}>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography
            variant="overline"
            sx={{
              display: 'block',
              color: 'text.secondary',
              letterSpacing: '0.08em',
              fontWeight: 600,
              lineHeight: 1.3,
              mb: 0.5,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {label}
          </Typography>
          <Typography
            variant="body1"
            sx={{
              fontWeight: 600,
              color: 'text.primary',
              lineHeight: 1.2,
              fontFeatureSettings: '"tnum"',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {value}
          </Typography>
          {sub && (
            <Typography
              variant="caption"
              sx={{
                display: 'block',
                color: 'text.secondary',
                lineHeight: 1.35,
                mt: 0.25,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {sub}
            </Typography>
          )}
        </Box>
        {icon && (
          <Box
            aria-hidden
            sx={{
              flexShrink: 0,
              width: 28,
              height: 28,
              borderRadius: 1,
              backgroundColor: alpha(color, 0.12),
              color,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              '& svg': { fontSize: iconSizes.md },
            }}
          >
            {icon}
          </Box>
        )}
      </Box>
    </Box>
  );
}

KpiTile.displayName = 'KpiTile';
KpiTile.Skeleton = KpiTileSkeleton;

export { KpiTileSkeleton };
export default KpiTile;
